/**
 * User Management Page — Organization Users & Invitations
 *
 * Lists organization users (GET /api/v1/impact-core/users/)
 * Invite new users via POST /api/v1/impact-core/invite/
 * Shows pending invitations alongside active users.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  X,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { ICRole } from '@/types';

interface Invitation {
  id: number;
  email: string;
  role: string;
  status: string;
  invited_at?: string;
  accepted_at?: string;
}

const ROLE_LABELS: Record<string, string> = {
  org_admin: 'Org Admin',
  fund_manager: 'Fund Manager',
  investment_officer: 'Investment Officer',
  impact_officer: 'Impact Officer',
  compliance_officer: 'Compliance Officer',
  investee_reporter: 'Investee Reporter',
  lp_viewer: 'LP Viewer',
  auditor: 'Auditor',
};

const ROLE_VARIANTS: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  org_admin: 'warning',
  fund_manager: 'success',
  investment_officer: 'info',
  impact_officer: 'info',
  compliance_officer: 'info',
  investee_reporter: 'default',
  lp_viewer: 'default',
  auditor: 'default',
};

const INVITABLE_ROLES: ICRole[] = [
  'fund_manager',
  'investment_officer',
  'impact_officer',
  'compliance_officer',
  'investee_reporter',
  'lp_viewer',
  'auditor',
];

export function UserManagementPage() {
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);

  // No /users/ endpoint exists — use /invite/ to get all invitations as team member list
  const invitationsQuery = useQuery<Invitation[]>({
    queryKey: ['ic', 'invitations'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/invite/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
  });

  const invitations = invitationsQuery.data ?? [];
  const acceptedMembers = invitations.filter((inv) => inv.status === 'accepted');
  const pendingInvitations = invitations.filter((inv) => inv.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">User Management</h1>
          <p className="text-xs text-ic-text-tertiary">
            {acceptedMembers.length} member{acceptedMembers.length !== 1 ? 's' : ''}
            {pendingInvitations.length > 0 && (
              <span> &middot; {pendingInvitations.length} pending invitation{pendingInvitations.length !== 1 ? 's' : ''}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
        >
          <UserPlus className="h-3.5 w-3.5" />
          Invite User
        </button>
      </div>

      {/* Team Members (from accepted invitations) */}
      <div>
        <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
          <Users className="h-3.5 w-3.5" />
          Team Members
        </h2>
        <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
          {invitationsQuery.isLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
            </div>
          ) : invitationsQuery.isError ? (
            <div className="flex h-48 items-center justify-center">
              <p className="text-xs text-red-400">Failed to load team members.</p>
            </div>
          ) : acceptedMembers.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center gap-2">
              <Users className="h-5 w-5 text-ic-text-tertiary" />
              <p className="text-xs text-ic-text-tertiary">No team members yet. Invite your first team member.</p>
            </div>
          ) : (
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">Email</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Role</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {acceptedMembers.map((member) => (
                  <tr key={member.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-2">
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-ic-accent/15 text-[10px] font-semibold text-ic-accent">
                          {member.email.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-xs font-medium text-ic-text-primary">{member.email}</span>
                      </div>
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge
                        label={ROLE_LABELS[member.role] ?? member.role}
                        variant={ROLE_VARIANTS[member.role] ?? 'default'}
                      />
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge label="Active" variant="success" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <div>
          <h2 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
            <Mail className="h-3.5 w-3.5" />
            Pending Invitations
          </h2>
          <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
            <table className="w-full text-xs ic-table">
              <thead>
                <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                  <th className="px-4 pb-2 pt-3 font-medium">Email</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Role</th>
                  <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvitations.map((inv) => (
                  <tr key={inv.id} className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50">
                    <td className="py-2.5 px-4 text-xs text-ic-text-primary">{inv.email}</td>
                    <td className="py-2.5 px-4">
                      <StatusBadge
                        label={ROLE_LABELS[inv.role] ?? inv.role}
                        variant={ROLE_VARIANTS[inv.role] ?? 'default'}
                      />
                    </td>
                    <td className="py-2.5 px-4">
                      <StatusBadge label="Pending" variant="warning" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <InviteUserModal
          onClose={() => setShowInviteModal(false)}
          onInvited={() => {
            setShowInviteModal(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'invitations'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Invite User Modal ────────────────────────────────────────

function InviteUserModal({ onClose, onInvited }: { onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ICRole>('fund_manager');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inviteMutation = useMutation({
    mutationFn: async (payload: { email: string; ic_role: string }) => {
      const res = await apiClient.post('/api/v1/impact-core/invite/', payload);
      return res.data;
    },
    onSuccess: () => {
      onInvited();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string | Array<{ msg: string; loc: string[] }> } } };
      const detail = axiosErr.response?.data?.detail;
      if (typeof detail === 'string') {
        setErrors({ _general: detail });
      } else if (Array.isArray(detail)) {
        const mapped: Record<string, string> = {};
        for (const item of detail) {
          const field = item.loc[item.loc.length - 1] ?? '_general';
          mapped[field] = item.msg;
        }
        setErrors(mapped);
      } else {
        setErrors({ _general: 'Failed to send invitation. Please try again.' });
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!email.trim()) {
      setErrors({ email: 'Email address is required' });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    inviteMutation.mutate({ email: email.trim(), ic_role: role });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">Invite User</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          <InviteFieldGroup label="Email Address *" error={errors.email}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@example.com"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </InviteFieldGroup>

          <InviteFieldGroup label="Role *" error={errors.role}>
            <div className="relative">
              <Shield className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary" />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as ICRole)}
                className="w-full appearance-none rounded-md border border-ic-border bg-ic-input py-2 pl-8 pr-3 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                {INVITABLE_ROLES.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
                ))}
              </select>
            </div>
          </InviteFieldGroup>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={inviteMutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InviteFieldGroup({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}
